import { ArrowBack } from '@mui/icons-material';
import { Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import { cloneDeep, get, set } from 'lodash';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import validator from 'validator';
import { ResetPassword } from '../../../lib/api/AuthUser';
import { PAGES_URLS } from '../../../lib/constants/routes';
import { FormStrucItem } from '../../../lib/types/types';
import CreateForm from '../../ui-component/form-component/create-form/CreateForm';
import LoadingBackdrop from '../../ui-component/loading/LoadingBackdrop';
import MUIAlert from '../../ui-component/mui-alert/MUIAlert';

const P = {
	attributes: {
		password: {
			label: 'Passwort',
			type: 'string',
			fieldType: 'password',
			path: 'attributes.password',
		},
		repeatPassword: {
			label: 'Passwort (Wiederholung)',
			type: 'string',
			fieldType: 'password',
			path: 'attributes.repeatPassword',
		},
	},
};

function useQuery() {
	const { search } = useLocation();
	return React.useMemo(() => new URLSearchParams(search), [search]);
}
function SaveNewPassword() {
	const navigate = useNavigate();
	const queryParams = useQuery();

	const [loading, setLoading] = useState(false);

	const [formValues, setFormValues] = useState({});
	const [formErrors, setFormErrors] = useState({});

	const [wsError, setWsError] = useState<Boolean | null>(null);
	const [wsSuccess, setWsSuccess] = useState<Boolean | null>(null);

	const handleInputChange = (key: string, value: any) => {
		let formValuesCopy = cloneDeep(formValues);
		set(formValuesCopy, key, value);
		let errorObject = cloneDeep(formErrors);
		delete errorObject[key];
		setFormValues(formValuesCopy);
		setFormErrors(errorObject);
		setWsError(null);
	};
	const validatePassword = () => {
		let success = false;
		if (get(formValues, P.attributes.password.path, '') === '' || get(formValues, P.attributes.repeatPassword.path, '') === '') {
			setFormErrors({
				[P.attributes.password.path]: 'Passwort darf nicht leer sein.',
				[P.attributes.repeatPassword.path]: 'Passwort darf nicht leer sein.',
			});
		} else if (get(formValues, P.attributes.password.path) !== get(formValues, P.attributes.repeatPassword.path)) {
			setFormErrors({
				[P.attributes.repeatPassword.path]: 'Beide Passwörter stimmen nicht überein.',
			});
		} else if (
			!validator.isStrongPassword(get(formValues, P.attributes.password.path, ''), {
				minLength: 8,
				minLowercase: 1,
				minUppercase: 1,
				minNumbers: 1,
				minSymbols: 1,
			})
		) {
			setFormErrors({
				[P.attributes.password.path]:
					'Ihr Passwort sollte 8 Zeichen lang sein und mindestens ein Symbol, einen Großbuchstaben, einen Kleinbuchstaben und eine Zahl enthalten.',
			});
		} else {
			success = true;
		}

		return success;
	};

	const handleFormSubmit = async () => {
		if (validatePassword()) {
			try {
				setLoading(true);
				const passwordData = {
					code: queryParams.get('code'), // code contained in the reset link of step 3.
					password: get(formValues, P.attributes.password.path, ''),
					passwordConfirmation: get(formValues, P.attributes.repeatPassword.path, ''),
				};
				let res = await ResetPassword(passwordData);
				if (res) setWsSuccess(true);
			} catch (err: any) {
				console.error('ERROR: name ', err);
				setWsError(true);
			} finally {
				setLoading(false);
			}
		}
	};

	if (!queryParams.get('code')) {
		return (
			<Container>
				<Paper sx={{ padding: '2rem', textAlign: 'center' }}>
					<Typography>Ungültige Token. </Typography>
					<Button startIcon={<ArrowBack />} onClick={() => navigate(`/${PAGES_URLS.Login}`)} sx={{ mt: '1rem' }}>
						Zurück zur Login
					</Button>
				</Paper>
			</Container>
		);
	}
	if (wsSuccess) {
		return (
			<Container>
				<MUIAlert
					infoType={'success'}
					title='Passwort wurde gesetzt'
					message='Ihr neues Passwort wurde gespeichert. Sie können sich nun anmelden'
				/>
				<Button startIcon={<ArrowBack />} onClick={() => navigate(`/${PAGES_URLS.Login}`)} sx={{ mt: '1rem' }}>
					Zurück zur Login
				</Button>
			</Container>
		);
	}
	if (wsError) {
		return (
			<Container>
				<MUIAlert infoType={'error'} message='Etwas ist schief gelaufen' />
				<Button startIcon={<ArrowBack />} onClick={() => navigate(`/${PAGES_URLS.Login}`)} sx={{ mt: '1rem' }}>
					Zurück zur Login
				</Button>
			</Container>
		);
	}

	return (
		<Container>
			<LoadingBackdrop open={loading} />

			<Stack direction={'row'} justifyContent={'space-between'} sx={{ mt: '2rem' }}>
				<Typography variant='h3' component={'h1'}>
					{' '}
					Neues Passwort speichern
				</Typography>
				<Button variant={'outlined'} onClick={() => navigate(PAGES_URLS.Login)} startIcon={<ArrowBack />}>
					Zurück zur Login-Seite
				</Button>
			</Stack>

			<Paper sx={{ mt: '3rem', p: '2rem', backgroundColor: '#FFF' }}>
				<Box>
					<Typography>Geben Sie hier Ihr neues Passswort ein.</Typography>
					<Typography>
						<strong>Bitte beachten Sie:</strong> Ihr Passwort sollte mindesten 8 Zeichen lang sein und mindestens ein Symbol, einen
						Großbuchstaben, einen Kleinbuchstaben und eine Zahl enthalten.
					</Typography>
				</Box>

				<CreateForm formStruc={formStruc()} formValues={formValues} updateFormValuesFunc={handleInputChange} formErrors={formErrors} />

				<Stack direction={'row'} justifyContent={'flex-end'} sx={{ mt: '2rem' }}>
					<Button onClick={handleFormSubmit}> Speichern</Button>
				</Stack>
			</Paper>
		</Container>
	);
}

export default SaveNewPassword;

const formStruc = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [P.attributes.password, P.attributes.repeatPassword],
		},
	];
	return struc;
};
